import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowPathIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { getBooklets } from "../../services/bookletService";
import { getQuestionFolders } from "../../services/questionService";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import { useRowLink } from "../../utils/rowLink";
import Select from "../../components/forms/Select/Select";
import { filterSelectStyles } from "../../components/forms/Select/styles";
import FilterChips from "../../components/QuestionList/FilterChips";
import FilterPanel from "../../components/QuestionList/FilterPanel";
import ResultsBar, { Pager } from "../../components/QuestionList/ResultsBar";
import { Dash } from "../../components/QuestionList/cells";
import { HelpTitle } from "../../components/Help/HelpButton";
import BtsGuide from "../../components/Help/guides/BtsGuide";
import { focusRing, Spinner, useMediaQuery } from "../Exams/TestBuilder/ui";
import {
  BOOKLET_STATUSES,
  BookletStatusBadge,
  bookletHref,
  bookletMeta,
  bookletFolders,
  Codes,
  GRADES,
  LANGUAGES,
  languageLabel,
  STATUS_TONES,
} from "./bts";

// The whole list is loaded (booklets are few: a BTS, a grade, a stream), filtered here and shown by pages.
const PAGE_SIZE = 30;
const DEFAULT_ORDERING = "-updated";
const ORDERINGS = ["-updated", "-created", "created", "name", "-name"];
const LIVE_JOB = ["queued", "running"];

// The page's one main button: the whole row on phones.
const createButtonClass = `inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 max-sm:h-11 max-sm:w-full ${focusRing}`;
const clearButtonClass = `mt-4 inline-flex h-9 items-center rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;
const headerCellClass = "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500";
const cellClass = "whitespace-nowrap px-4 py-3 text-sm";
const panelClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";

const time = (value) => {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const sorter = (ordering, collator) => {
  const field = ordering.replace(/^-/, "");
  const direction = ordering.startsWith("-") ? -1 : 1;
  if (field === "name") return (a, b) => direction * collator.compare((a.name || "").trim(), (b.name || "").trim());
  return (a, b) => direction * (time(a[field]) - time(b[field]));
};

// How far the booklet's PDFs are: a job running, the current files, or the last job failed.
export function PdfState({ documents, compact = false }) {
  const [t] = useTranslation();
  const job = documents?.job;
  const current = documents?.current || 0;
  if (job && LIVE_JOB.includes(job.status)) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-blue-700">
        <Spinner className="h-3.5 w-3.5" />
        {t("bts.pdf.running", { done: job.done || 0, total: job.total || 0 })}
      </span>
    );
  }
  const failed = job?.status === "failed";
  if (!current && !failed) return compact ? null : <Dash />;
  return (
    <span className="inline-flex flex-col gap-0.5 text-sm">
      {current > 0 && (
        <span className="inline-flex items-center gap-1.5 text-slate-700">
          <DocumentDuplicateIcon className="h-4 w-4 text-slate-400" aria-hidden="true" />
          {t("bts.pdf.files", { count: current })}
        </span>
      )}
      {failed && (
        <span className="inline-flex items-center gap-1.5 text-red-700">
          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" />
          {t("bts.pdf.failed")}
        </span>
      )}
    </span>
  );
}

function EmptyState({ Icon, title, hint, children }) {
  return (
    <div className={`flex flex-col items-center justify-center px-6 py-16 text-center ${panelClass}`}>
      <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <h3 className="text-sm font-medium text-slate-900">{title}</h3>
      {hint && <p className="mt-1 max-w-md text-sm text-slate-500">{hint}</p>}
      {children}
    </div>
  );
}

// Admin: the BTS booklets, filtered by BTS folder, grade, status and stream (the filters live in the address).
export default function BtsList() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const rowLink = useRowLink();
  const wide = useMediaQuery("(min-width: 1024px)");
  const resultsRef = useRef(null);
  const [params, setParams] = useSearchParams();
  // The booklet and create pages lead back to the list as it is now (its filters live in the address).
  const fromList = { listSearch: useLocation().search };

  const [booklets, setBooklets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const folder = params.get("folder") || "all";
  const grade = params.get("grade") || "all";
  const stream = params.get("language") || "all";
  const status = BOOKLET_STATUSES.includes(params.get("status")) ? params.get("status") : "all";
  const search = params.get("q") || "";
  const ordering = ORDERINGS.includes(params.get("ordering")) ? params.get("ordering") : DEFAULT_ORDERING;
  const page = Math.max(1, Number(params.get("page")) || 1);

  // One filter changed (the others kept); "all" and the defaults leave the address. A filter starts on page 1.
  const update = (changes, { keepPage = false } = {}) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === "all" || value === "" || value === null || (key === "ordering" && value === DEFAULT_ORDERING) || (key === "page" && value === 1)) {
            next.delete(key);
          } else next.set(key, String(value));
        });
        if (!keepPage) next.delete("page");
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    Promise.all([
      fetchAllPages((page) => getBooklets({ params: page, signal: controller.signal })),
      getQuestionFolders({ signal: controller.signal }).catch(() => []),
    ])
      .then(([rows, folderList]) => {
        if (!active) return;
        setBooklets(Array.isArray(rows) ? rows : []);
        setFolders(Array.isArray(folderList) ? folderList : folderList?.results || []);
        setError(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching booklets:", err);
        setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadKey]);

  const collator = useMemo(() => new Intl.Collator(language, { numeric: true, sensitivity: "base" }), [language]);
  const query = search.trim().toLowerCase();

  // The BTS folders, and any other folder a booklet is in.
  const folderOptions = useMemo(() => {
    const options = bookletFolders(folders).map((f) => ({ value: f.uuid, label: f.name }));
    booklets.forEach((b) => {
      if (b.folder?.uuid && !options.some((o) => o.value === b.folder.uuid)) options.push({ value: b.folder.uuid, label: b.folder.name });
    });
    return options;
  }, [folders, booklets]);

  // What matches every filter but the status: the status chips count these.
  const matching = useMemo(
    () =>
      booklets.filter(
        (b) =>
          (folder === "all" || b.folder?.uuid === folder) &&
          (grade === "all" || String(b.grade) === grade) &&
          (stream === "all" || b.language === stream) &&
          (!query || [b.name, b.description, ...(b.codes || [])].some((text) => (text || "").toLowerCase().includes(query)))
      ),
    [booklets, folder, grade, stream, query]
  );
  const statusCounts = useMemo(() => {
    const counts = {};
    matching.forEach((b) => {
      counts[b.status] = (counts[b.status] || 0) + 1;
    });
    return counts;
  }, [matching]);
  const results = useMemo(
    () => (status === "all" ? matching : matching.filter((b) => b.status === status)).slice().sort(sorter(ordering, collator)),
    [matching, status, ordering, collator]
  );

  const pageCount = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const filtersActive = folder !== "all" || grade !== "all" || stream !== "all" || status !== "all" || !!query;
  const clearAll = () => update({ folder: "all", grade: "all", language: "all", status: "all", q: "" });

  const goToPage = (next, { scroll = false } = {}) => {
    update({ page: next }, { keepPage: true });
    if (!scroll) return;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    resultsRef.current?.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
  };

  const allFolders = [{ value: "all", label: t("bts.list.all_folders") }, ...folderOptions];
  const allGrades = [{ value: "all", label: t("bts.list.all_grades") }, ...GRADES.map((g) => ({ value: String(g), label: t("bts.grade_value", { grade: g }) }))];
  const allLanguages = [{ value: "all", label: t("bts.list.all_languages") }, ...LANGUAGES.map((l) => ({ value: l, label: t(`bts.language_names.${l}`) }))];
  const filterSelect = (key, label, options, value) => ({
    key,
    active: value !== "all",
    node: (
      <Select
        ariaLabel={label}
        customStyles={filterSelectStyles}
        options={options}
        value={value}
        onChange={(option) => update({ [key]: option?.value || "all" })}
        isSearchable={options.length > 10}
      />
    ),
  });
  const filters = [
    filterSelect("folder", t("bts.fields.folder"), allFolders, folder),
    filterSelect("grade", t("bts.fields.grade"), allGrades, grade),
    filterSelect("language", t("bts.fields.language"), allLanguages, stream),
  ];

  const sortOptions = [
    { value: "-updated", label: t("bts.list.sort_updated") },
    { value: "-created", label: t("question_list.sort_options.newest") },
    { value: "created", label: t("question_list.sort_options.oldest") },
    { value: "name", label: t("question_list.sort_options.name_asc") },
    { value: "-name", label: t("question_list.sort_options.name_desc") },
  ];
  const statusItems = BOOKLET_STATUSES.map((value) => ({
    value,
    label: t(`bts.status.${value}`),
    count: statusCounts[value] || 0,
    tone: STATUS_TONES[value],
  }));

  const nameLink = (b, className = "") => (
    <Link
      to={bookletHref(b.uuid)}
      state={fromList}
      className={`rounded font-medium text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing} ${className}`}
    >
      {b.name?.trim() || t("bts.untitled")}
    </Link>
  );

  let list;
  if (results.length === 0) {
    list = (
      <EmptyState Icon={MagnifyingGlassIcon} title={t("bts.list.no_results")} hint={t("bts.list.no_results_hint")}>
        {filtersActive && (
          <button type="button" onClick={clearAll} className={clearButtonClass}>
            {t("question_list.clear_all")}
          </button>
        )}
      </EmptyState>
    );
  } else if (wide) {
    list = (
      <div className="relative min-w-0 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200">
          <caption className="sr-only">{t("nav.bts")}</caption>
          <thead className="bg-slate-50">
            <tr>
              <th scope="col" className={`${headerCellClass} w-full`}>
                {t("bts.columns.name")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.fields.folder")}
              </th>
              <th scope="col" className={`${headerCellClass} text-right`}>
                {t("bts.fields.grade")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.fields.language")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.columns.codes")}
              </th>
              <th scope="col" className={`${headerCellClass} text-right`}>
                {t("bts.columns.sections")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.columns.status")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.columns.pdf")}
              </th>
              <th scope="col" className={headerCellClass}>
                {t("bts.columns.updated")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageItems.map((b) => (
              <tr key={b.uuid} {...rowLink(bookletHref(b.uuid), { state: fromList })} className="cursor-pointer transition-colors duration-150 hover:bg-slate-50/80">
                <td className="px-4 py-3 text-sm">
                  <div className="min-w-[16rem] max-w-3xl break-words">
                    {nameLink(b)}
                    {b.description?.trim() && <p className="mt-0.5 line-clamp-1 text-slate-500">{b.description}</p>}
                  </div>
                </td>
                <td className={`${cellClass} text-slate-700`}>{b.folder?.name || <Dash />}</td>
                <td className={`${cellClass} text-right tabular-nums text-slate-700`}>{b.grade ?? <Dash />}</td>
                <td className={`${cellClass} text-slate-700`}>{languageLabel(t, b.language) || <Dash />}</td>
                <td className="px-4 py-3 text-sm">
                  {b.codes?.length ? <Codes codes={b.codes} className="min-w-[8rem] max-w-[16rem]" /> : <Dash />}
                </td>
                <td className={`${cellClass} text-right tabular-nums ${b.sections_count ? "font-medium text-slate-700" : "text-slate-400"}`}>
                  {b.sections_count ?? 0}
                </td>
                <td className={cellClass}>
                  <BookletStatusBadge status={b.status} />
                </td>
                <td className={cellClass}>
                  <PdfState documents={b.documents} />
                </td>
                <td className={`${cellClass} tabular-nums text-slate-500`} title={formatDateTime(b.updated, language)}>
                  {formatDate(b.updated, language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else {
    list = (
      <ul className="flex flex-col gap-2.5">
        {pageItems.map((b) => (
          <li
            key={b.uuid}
            {...rowLink(bookletHref(b.uuid), { state: fromList })}
            className={`relative cursor-pointer break-words p-3.5 transition duration-200 hover:border-slate-300 ${panelClass}`}
          >
            <div className="flex items-start justify-between gap-3">
              {/* The name's link covers the whole card (it holds no other control): the card is one tap target, a
                  real link (long press, new tab), with the focus ring on the name. */}
              {nameLink(b, "text-[15px] font-semibold leading-snug after:absolute after:inset-0 after:rounded-2xl")}
              <BookletStatusBadge status={b.status} />
            </div>
            <p className="mt-1 text-sm text-slate-600">{bookletMeta(t, b)}</p>
            {b.codes?.length > 0 && <Codes codes={b.codes} className="mt-2" />}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
              <span>{t("bts.list.sections_count", { count: b.sections_count || 0 })}</span>
              <PdfState documents={b.documents} compact />
              <span className="tabular-nums">{t("bts.list.updated_on", { date: formatDate(b.updated, language) })}</span>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  // A new booklet starts with the folder, grade and stream filtered here.
  const createParams = new URLSearchParams(
    Object.entries({ folder, grade, language: stream }).filter(([, value]) => value !== "all")
  ).toString();
  const createHref = `/bts/new${createParams ? `?${createParams}` : ""}`;
  const emptyList = !loading && !error && booklets.length === 0;

  let body;
  if (loading && !booklets.length) {
    body = (
      <div className={`flex items-center justify-center py-16 ${panelClass}`}>
        <div className="text-center" role="status">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true" />
          <p className="text-sm text-slate-500">{t("bts.list.loading")}</p>
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">{t("bts.list.load_error")}</span>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className={`inline-flex h-9 items-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-red-700 ring-1 ring-inset ring-red-200 transition duration-200 hover:bg-red-100 ${focusRing}`}
        >
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t("bts.retry")}
        </button>
      </div>
    );
  } else if (emptyList) {
    body = (
      <EmptyState Icon={DocumentDuplicateIcon} title={t("bts.list.empty_title")} hint={t("bts.list.empty_hint")}>
        <Link to={createHref} state={fromList} className={`${createButtonClass} mt-4`}>
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
          {t("bts.list.create")}
        </Link>
      </EmptyState>
    );
  } else {
    body = (
      <>
        <FilterChips
          label={t("question_list.status_filter")}
          allLabel={t("question_list.status_all")}
          allCount={matching.length}
          items={statusItems}
          selected={status}
          onSelect={(value) => update({ status: value })}
        />
        <FilterPanel
          search={search}
          onSearchChange={(value) => update({ q: value })}
          searchPlaceholder={t("bts.list.search_placeholder")}
          searchLabel={t("bts.list.search_label")}
          filters={filters}
          canClear={filtersActive}
          onClearAll={clearAll}
        />
        <div ref={resultsRef} className="scroll-mt-4 lg:scroll-mt-6">
          <ResultsBar
            countText={t("bts.list.count", { count: results.length })}
            page={currentPage}
            pageCount={pageCount}
            ordering={ordering}
            onOrderingChange={(value) => update({ ordering: value })}
            sortOptions={sortOptions}
            onPage={goToPage}
          />
        </div>
        {list}
        {pageItems.length > 0 && (
          <div className="flex justify-center">
            <Pager
              page={currentPage}
              pageCount={pageCount}
              onPage={(next) => goToPage(next, { scroll: true })}
              label={t("question_list.pagination_bottom")}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {/* The guide's "?" right after the title (the same guide on the booklet and create pages). */}
          <HelpTitle helpId="bts" helpTitle={t("help.bts.title")} guide={<BtsGuide />}>
            {t("nav.bts")}
          </HelpTitle>
          <p className="mt-1 text-sm text-slate-500">{t("bts.list.intro")}</p>
        </div>
        {!emptyList && (
          <Link to={createHref} state={fromList} className={createButtonClass}>
            <PlusIcon className="h-5 w-5" aria-hidden="true" />
            {t("bts.list.create")}
          </Link>
        )}
      </div>
      {body}
    </div>
  );
}
